import React, { useState, useMemo, useCallback } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Divider from '@material-ui/core/Divider';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';
import SimpleBar from 'simplebar-react';
import useMeasure from 'react-use-measure';
import useTimer from '../../hooks/useTimer';
import { baseMoment, toAPStyleTime } from '../../helpers/dates';
// import { selectEventTimeFormat } from '../../helpers/selectors';
import BlockGroup from './BlockGroup';
import Workshop from './Workshop';
import Meeting from './Meeting';
import PendingMeeting from './PendingMeeting';
import PendingRequests from './PendingRequests';
import BlockedSlot from './BlockedSlot';
import AvailableSlot from './AvailableSlot';
import OwnSlot from './OwnSlot';
import {
  minutesToPixels,
  SLOT_HEIGHT,
  DIVIDER_HEIGHT,
  BLOCK_WIDTH,
  BLOCK_SPACING_X,
} from './Block';
import { selectEventTimezone } from '../../helpers/selectors';

const HOUR_COLUMN_WIDTH = 82;
const MOBILE_HOUR_COLUMN_WIDTH = 50;

const blocksIntersect = (
  b1, // less than or equal to b2 in the array
  b2
) => !!b1 && !!b2 && baseMoment(b2.startDate).isBefore(b1.endDate);

const distributeBlocks = (blocks) => {
  if (!blocks || !blocks.length) return { distribution: {}, groups: {} };
  const distribution = {};
  const intersections = {};
  const colsForGroup = {};
  const groups = {};
  let group = 0;
  blocks.forEach((block, i) => {
    const pastBlocks = blocks.slice(0, i);
    const pastIntersections = pastBlocks.filter(
      (pastBlock) => blocksIntersect(pastBlock, block)
    );

    if (pastIntersections.length === 0) group += 1;

    if (!colsForGroup[group]) colsForGroup[group] = 1;
    const colsNeeded = pastIntersections.length + 1;
    if (colsForGroup[group] < (colsNeeded)) colsForGroup[group] = colsNeeded;

    distribution[block.key] = {
      group: String(group),
    };
    intersections[block.key] = pastIntersections;

    if (!groups[group]) groups[group] = {
      blockIds: [block.key],
      startDate: block.startDate,
    };
    else groups[group].blockIds.push(block.key);
    groups[group].endDate = (
      groups[group].endDate
      && baseMoment(groups[group].endDate).isAfter(block.endDate)
    )
      ? groups[group].endDate
      : block.endDate;
  });

  blocks.forEach((block) => {
    let offset = 0;
    const usedOffsets = intersections[block.key].map(
      ({ key }) => distribution[key].offset
    );
    while (usedOffsets.includes(offset)) offset += 1;
    distribution[block.key].offset = offset;
    distribution[block.key].colsForGroup = colsForGroup[distribution[block.key].group];
  });

  return { distribution, groups };
};

const contentTypes = {
  workshop: Workshop,
  meeting: Meeting,
  blockedSlot: BlockedSlot,
  availableSlot: AvailableSlot,
  ownSlot: OwnSlot,
  pendingMeeting: PendingMeeting,
  request: PendingRequests,
};

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#F1F2F1',
    position: 'relative',
    width: '100%',
    marginTop: theme.spacing(1),
    marginLeft: HOUR_COLUMN_WIDTH,
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(-theme.subpageDrawer.paddings.desktop.horizontal),
      borderRadius: '10px 0 0 10px',
    },
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(-theme.subpageDrawer.paddings.mobile.horizontal),
      marginLeft: MOBILE_HOUR_COLUMN_WIDTH,
    },
  },
  unextendedContainer: {
    marginRight: 0,
  },
  slot: {
    color: theme.palette.darkGrey.light,
    position: 'relative',
    height: SLOT_HEIGHT,
    '&:first-of-type $divider': {
      visibility: 'hidden',
    },
    '&:last-of-type': {
      height: 0,
      '& $divider': {
        visibility: 'hidden',
      },
    },
  },
  divider: {
    backgroundColor: '#E8E9E8',
    height: DIVIDER_HEIGHT,
  },
  timeSlot: {
    fontSize: 17,
    fontWeight: 400,
    lineHeight: '100%',
    letterSpacing: '0.04em',
    height: theme.spacing(1.5),
    position: 'absolute',
    left: -HOUR_COLUMN_WIDTH,
    width: HOUR_COLUMN_WIDTH,
    top: -6,
    fontFamily: 'Fancy Cut Pro',
    display: 'flex',
    alignItems: 'baseline',
    [theme.breakpoints.down('sm')]: {
      top: -3,
      fontSize: 10,
      left: -MOBILE_HOUR_COLUMN_WIDTH,
      width: MOBILE_HOUR_COLUMN_WIDTH,
    },
  },
  timePeriod: {
    paddingLeft: theme.spacing(0.25),
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
    },
  },
  currentTimeMarker: {
    position: 'absolute',
    width: '100%',
    height: 16,
    zIndex: 1,
    pointerEvents: 'none',
  },
  currentTimeCircle: {
    padding: theme.spacing(0, 0.5, 0, 0.5),
    transform: 'translate(calc(-100% - 9px), -3.5px)',
    height: 23,
    backgroundColor: theme.palette.primary.main,
    // borderRadius: 10,
    color: theme.palette.common.white,
    lineHeight: 1.3,
    display: 'inline-block',
    fontSize: 17,
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
  },
  currentTimeLine: {
    height: 2,
    backgroundColor: theme.palette.primary.main,
    width: 'calc(100% + 9px)',
    position: 'absolute',
    left: -9,
    top: 7,
  },
  scrollContainer: {
    position: 'absolute',
    top: 0,
    width: '100%',
    height: '100%',
    '& .simplebar-track.simplebar-horizontal': {
      borderRadius: 10,
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      position: 'sticky',
      bottom: -32,
      marginTop: -15,
      height: 15,
      [theme.breakpoints.down('sm')]: {
        bottom: 0,
      },
      '&.simplebar-hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
      },
      '&.simplebar-hover .simplebar-scrollbar:before': {
        opacity: 0.6,
      },
    },
    '& .simplebar-track.simplebar-horizontal .simplebar-scrollbar': {
      height: 11,
    },
  },
}));

const Schedule = ({
  activities,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const eventTimezone = useSelector(selectEventTimezone);
  const [inRange, setInRange] = useState(false);
  const [scheduleWidthRef, bounds] = useMeasure();
  const scheduleWidth = bounds?.width;

  const [currentTime, setCurrentTime] = useState('');
  const stepSize = useMemo(() => {
    const minDuration = Math.min(...(activities.map(
      ({ startDate, endDate }) => baseMoment(endDate).diff(startDate, 'minutes')
    )));

    if (minDuration <= 10) return isMobile ? 3 : 5;
    if (minDuration <= 30) return isMobile ? 12 : 20;
    return isMobile ? 15 : 30;
  }, [activities, isMobile]);
  const { distribution, groups } = useMemo(() => distributeBlocks(activities), [activities]);

  const fullWidthBlocks = useMemo(() => (
    Math.max(...(activities.map((activity) => (
      distribution[activity.key].offset
    )))) === 0
  ), [activities, distribution]);

  const visibleHoursInterval = (stepSize < 30) ? 30 : 60;
  // const hourFormat = useSelector(selectEventTimeFormat());
  // const currentTimeFormat = useSelector(selectEventTimeFormat({ includeMinutes: true }));

  const { startDate } = activities.slice(0, 1)[0] || {};
  const scheduleStart = useMemo(() => {
    const start = baseMoment.utc(startDate).tz(eventTimezone);
    start.minute(0);
    return start;
  }, [startDate, eventTimezone]);

  const endDates = activities.map(({ endDate }) => baseMoment(endDate).valueOf());
  const endDate = endDates.length ? Math.max(...endDates) : undefined;

  const scheduleEnd = useMemo(() => {
    const end = baseMoment.utc(endDate).tz(eventTimezone);
    end.subtract(1, 'minute');
    end.minute(0);
    end.add(1, 'hour');
    // add some steps in the end and guarantee minimum height
    const steps = end.diff(startDate, 'minutes') / stepSize;
    const stepsToAdd = Math.max(4 - steps, 0) + 2;
    end.add(stepsToAdd * stepSize, 'minutes');
    return end;
  }, [endDate, startDate, stepSize, eventTimezone]);

  const hours = useMemo(() => {
    const timeDiff = scheduleEnd.diff(scheduleStart, 'minutes');
    const span = Math.ceil(timeDiff / stepSize);
    return [...Array(span + 1).keys()].map(
      (step) => scheduleStart.clone().add(stepSize * step, 'minutes')
    );
  }, [scheduleStart, scheduleEnd, stepSize]);

  const markerTop = useMemo(() => {
    const elapsedMinutes = currentTime
      ? baseMoment(currentTime).diff(scheduleStart, 'minutes') : 0;
    return minutesToPixels(elapsedMinutes, stepSize);
  }, [currentTime, scheduleStart, stepSize]);

  const onTick = useCallback((now, start, end) => {
    if (now.isBetween(start, end, null, '[)')) {
      setCurrentTime(now.format());
      setInRange(true);
    } else setInRange(false);
  }, [setInRange, setCurrentTime]);
  useTimer({
    startDate: scheduleStart.format(),
    endDate: scheduleEnd.format(),
    onTick,
    step: 60000,
  });

  const maxMaxOffset = Math.max(...(activities.map((activity) => (
    distribution[activity.key].offset
  ))));

  const blockGroups = Object.keys(groups).map((groupId) => {
    const activitiesInGroup = activities.filter((activity) => (
      distribution[activity.key].group === groupId
    ));
    const maxOffset = Math.max(...(activitiesInGroup.map((activity) => (
      distribution[activity.key].offset
    ))));
    return (
      <BlockGroup
        key={groupId}
        startDate={groups[groupId].startDate}
        endDate={groups[groupId].endDate}
        scheduleStart={scheduleStart.format()}
        stepSize={stepSize}
        maxOffset={maxOffset}
      >
        {activitiesInGroup.map((activity) => {
          if (activity.type === 'emptyBlock') return null;
          const Content = contentTypes[activity.type];
          return (
            <Content
              {...activity}
              scheduleStart={groups[groupId].startDate}
              stepSize={stepSize}
              cols={distribution[activity.key].colsForGroup}
              offset={distribution[activity.key].offset}
              fullWidthBlocks={fullWidthBlocks}
              scheduleWidth={scheduleWidth}
            />
          );
        })}
      </BlockGroup>
    );
  });

  return (
    <div
      className={clsx(
        classes.container,
        fullWidthBlocks && classes.unextendedContainer
      )}
      ref={scheduleWidthRef}
    >
      {inRange && (
        <div className={classes.currentTimeMarker} style={{ top: markerTop }}>
          <div className={classes.currentTimeCircle}>
            {baseMoment.utc(currentTime).tz(eventTimezone).format('h:mm')}
            <span className={classes.timePeriod}>
              {toAPStyleTime(baseMoment.utc(currentTime).tz(eventTimezone).format('A'))}
            </span>
            {/* {baseMoment(currentTime).format(currentTimeFormat)} */}
          </div>
          <div className={classes.currentTimeLine} />
        </div>
      )}
      <div>
        {hours.map((hour) => (
          <div key={hour.format()} className={classes.slot}>
            <Divider className={classes.divider} />
            {(hour.minutes() % visibleHoursInterval === 0) && (
              <Typography className={classes.timeSlot}>
                {hour.format('h:mm')}
                <span className={classes.timePeriod}>
                  {toAPStyleTime(hour.format('A'))}
                </span>
              </Typography>
            )}
          </div>
        ))}
      </div>
      {!fullWidthBlocks ? (
        <SimpleBar
          className={classes.scrollContainer}
          autoHide={false}
          forceVisible
        >
          <div
            style={{
              width: `calc(${(maxMaxOffset + 1) * BLOCK_WIDTH}% + ${(maxMaxOffset + 2) * BLOCK_SPACING_X}px)`,
              height: 1,
            }}
          >
            {blockGroups}
          </div>
        </SimpleBar>
      ) : blockGroups}
    </div>
  );
};

Schedule.propTypes = {
  activities: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    type: PropTypes.string,
    onClick: PropTypes.func,
  })),
};

Schedule.defaultProps = {
  activities: [],
};

export default Schedule;
